import { inferReadonlyArray } from "@aprao/common/src/array/inferReadonlyArray.js";
import { pull } from "@aprao/common/src/array/pull.js";
import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { FRACTION_ONE } from "@aprao/common/src/math/fraction.js";
import { sum } from "@aprao/common/src/math/sum.js";
import { makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";
import { mapForNullable } from "option-t/nullable";

import type { ShapeBuildCostsPreliminary } from "../../shapes/ShapeBuildCostsPreliminary.js";
import { AmountNamedFacade } from "../AmountNamedFacade.js";
import { WithProjectionPeriodsDelegate } from "../delegates/WithProjectionPeriodsDelegate.js";

const createPreliminaryCost = (ordinal: number) => ({
  name: `Preliminary cost ${ordinal}`,
  value: null,
});

export class BuildCostsPreliminaryFacade {
  $model;

  $totalCostScale = FRACTION_ONE;

  constructor($model: ShapeBuildCostsPreliminary) {
    makeAutoObservable(this);

    this.$model = $model;
  }

  readonly _decorateCost = computedFn(
    (cost: ShapeBuildCostsPreliminary["costs"][number]) =>
      new AmountNamedFacade(cost),
  );

  get costs() {
    return inferReadonlyArray(
      this.$model.costs.map((cost) => this._decorateCost(cost)),
    );
  }

  get totalCost() {
    return mapForNullable(
      sum(this.costs.map(({ valueHolder }) => valueHolder.value)),
      (value) => value.mul(this.$totalCostScale),
    );
  }

  /**
   * Tweaks the value with the given ratio.
   *
   * This is only used to generate the sensitivity analysis and is not
   * persisted into the model.
   */
  rescaleTotalCost(ratio: BigFraction) {
    this.$totalCostScale = ratio;
  }

  get projectionPeriods() {
    return new WithProjectionPeriodsDelegate(this.$model);
  }

  addCost() {
    this.$model.costs.push(createPreliminaryCost(this.costs.length + 1));
  }

  removeCost(cost: AmountNamedFacade) {
    pull(this.$model.costs, cost.$model);
  }
}
